import { Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type {
  BridgeInsight,
  ComponentsResponse,
  GraphComponent,
  TopEntitiesResponse,
  TopEntityEntry,
} from '@/services/api/graph'
import { BridgesCard } from './bridges/BridgesCard'
import { ClusterCompositionChart } from './charts/ClusterCompositionChart'
import { ScoreDistributionChart } from './charts/ScoreDistributionChart'
import { ClusterExplorer } from './cluster-explorer/ClusterExplorer'
import { KpiStatRow } from './kpi/KpiStatRow'
import { StaleAnalysisBanner } from './kpi/StaleAnalysisBanner'
import { EntityLeaderboard } from './leaderboard/EntityLeaderboard'
import { ScoreBandLegend } from './primitives/ScoreBandLegend'
import type { BridgeGroup, ClusterComposition, KpiSummary, ScoreBucket } from './selectors'

export interface InsightsDashboardViewProps {
  caseId: string
  /** Initial data fetch in flight — show skeleton. */
  loading: boolean
  /** A recompute call is in flight. */
  computing: boolean
  /** The last fetch failed. */
  hadError: boolean
  /** True when there is no cluster / bridge / top-entity data to show. */
  isEmpty: boolean
  /** True when the analysis is stale relative to current documents. */
  isStale: boolean
  hasComponents: boolean
  components: ComponentsResponse | null
  topEntities: TopEntitiesResponse | null
  // ── derived data (computed by the connected wrapper) ──
  kpi: KpiSummary
  composition: ClusterComposition[]
  buckets: ScoreBucket[]
  bridgeGroups: BridgeGroup[]
  totalBridges: number
  biggestClusterLabel: string | null
  labelFor: (compId: number) => string
  // ── actions ──
  onRecompute: () => void
  onRetry: () => void
  onSelectCompositionCluster: (compId: number) => void
  onBridgeClick: (bridge: BridgeInsight) => void
  onRowClick: (entityType: string, entity: TopEntityEntry) => void
  onNavigateCluster: (component: GraphComponent, label: string) => void
  onOpenDocument?: (documentId: string) => void
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`rounded-md bg-[var(--muted)]/30 animate-pulse ${className ?? ''}`} />
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[0, 1, 2, 3, 4].map(i => (
          <Card key={i} className="space-y-3 p-4">
            <SkeletonBlock className="h-3 w-20" />
            <SkeletonBlock className="h-8 w-14" />
            <SkeletonBlock className="h-2 w-16" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {[0, 1].map(i => (
          <Card key={i} className="space-y-3 p-4">
            <SkeletonBlock className="h-4 w-40" />
            <SkeletonBlock className="h-3 w-60" />
            <SkeletonBlock className="h-40 mt-4" />
          </Card>
        ))}
      </div>
      <Card className="space-y-2 p-4">
        <SkeletonBlock className="h-4 w-48" />
        <SkeletonBlock className="h-3 w-80" />
        <SkeletonBlock className="h-8 mt-3" />
        {[0, 1, 2, 3, 4].map(i => (
          <SkeletonBlock key={i} className="h-8" />
        ))}
      </Card>
    </div>
  )
}

function EmptyCard({ onRecompute, computing }: { onRecompute: () => void; computing: boolean }) {
  return (
    <div className="py-12">
      <Card
        variant="dashed"
        className="max-w-md mx-auto p-8 flex flex-col items-center text-center gap-3"
      >
        <div className="size-12 rounded-full bg-[var(--primary)]/10 text-[var(--primary)] inline-flex items-center justify-center">
          <Sparkles className="size-5" />
        </div>
        <h3 className="text-base font-semibold text-[var(--foreground)]">No insights yet</h3>
        <p className="text-sm text-[var(--muted-foreground)] max-w-sm">
          Run analysis on this case to detect clusters, cross-cluster bridges, and the entities that
          hold them together.
        </p>
        <button
          type="button"
          onClick={onRecompute}
          disabled={computing}
          className="mt-2 h-9 px-4 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {computing ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Run analysis
        </button>
        <span className="text-3xs text-[var(--muted-foreground)]">
          Typically takes 10–40 s for a case this size.
        </span>
      </Card>
    </div>
  )
}

function ErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="py-12">
      <Card
        variant="dashed"
        className="max-w-md mx-auto p-8 flex flex-col items-center text-center gap-3"
      >
        <h3 className="text-base font-semibold text-[var(--foreground)]">Couldn't load insights</h3>
        <p className="text-sm text-[var(--muted-foreground)] max-w-sm">
          Check the network connection and retry — the page will refetch clusters, bridges, and top
          entities.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 h-9 px-4 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-sm font-medium inline-flex items-center gap-1.5"
        >
          <RefreshCw className="size-3.5" /> Retry
        </button>
      </Card>
    </div>
  )
}

/**
 * Pure presentation for the Insights dashboard. All data fetching, selectors,
 * and graph-navigation handoff live in the connected `features/insights`
 * wrapper — this component only renders props.
 */
export function InsightsDashboardView({
  caseId,
  loading,
  computing,
  hadError,
  isEmpty,
  isStale,
  hasComponents,
  components,
  topEntities,
  kpi,
  composition,
  buckets,
  bridgeGroups,
  totalBridges,
  biggestClusterLabel,
  labelFor,
  onRecompute,
  onRetry,
  onSelectCompositionCluster,
  onBridgeClick,
  onRowClick,
  onNavigateCluster,
  onOpenDocument,
}: InsightsDashboardViewProps) {
  const { t } = useTranslation()

  const toolbar = (
    <Button
      size="sm"
      variant={isEmpty || isStale ? 'default' : 'outline'}
      onClick={onRecompute}
      disabled={computing || loading}
      className="h-8 max-md:h-9"
    >
      {computing ? (
        <Loader2 className="size-3.5 mr-1.5 animate-spin" />
      ) : (
        <RefreshCw className="size-3.5 mr-1.5" />
      )}
      {isEmpty ? 'Run analysis' : t('insights.recompute')}
    </Button>
  )

  return (
    <PageShell
      layout="padded"
      icon={Sparkles}
      title={t('insights.title')}
      summary={t('insights.summary')}
      guide={{
        to: '/docs/investigation-analysis',
        label: 'Guide',
        hint: 'Insights surfaces the structure of your case graph — clusters, bridges, and the entities that hold it together.',
      }}
      toolbar={toolbar}
    >
      {loading ? (
        <DashboardSkeleton />
      ) : isEmpty ? (
        hadError ? (
          <ErrorCard onRetry={onRetry} />
        ) : (
          <EmptyCard onRecompute={onRecompute} computing={computing} />
        )
      ) : (
        <div className="space-y-4">
          {/* Zone 1: KPI strip */}
          <KpiStatRow summary={kpi} biggestClusterLabel={biggestClusterLabel} />

          {/* Stale banner (conditional) */}
          {isStale && (
            <StaleAnalysisBanner
              onRecompute={onRecompute}
              computing={computing}
              note="new documents have been added since the last computation"
            />
          )}

          {/* Zone 2: Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ClusterCompositionChart
              data={composition}
              labelFor={labelFor}
              onSelect={onSelectCompositionCluster}
              onEmptyAction={onRecompute}
            />
            <ScoreDistributionChart data={buckets} />
          </div>

          {/* Zone 3: Leaderboard */}
          <EntityLeaderboard caseId={caseId} data={topEntities} onRowClick={onRowClick} />

          {/* Zone 4: Bridges + Cluster explorer */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <BridgesCard
              groups={bridgeGroups}
              totalBridges={totalBridges}
              hasClusters={hasComponents}
              labelFor={labelFor}
              onBridgeClick={onBridgeClick}
              onOpenDocument={onOpenDocument}
            />
            <ClusterExplorer caseId={caseId} data={components} onOpenInGraph={onNavigateCluster} />
          </div>

          {/* Legend (sticky footer) */}
          <div className="bg-[var(--background)]/95 backdrop-blur border-t border-[var(--border)] px-4 py-2 -mx-4 flex flex-wrap items-center gap-1.5 md:sticky md:bottom-0 md:px-6 md:-mx-6">
            <ScoreBandLegend />
          </div>
        </div>
      )}
    </PageShell>
  )
}
